import { useState, type ReactNode } from 'react'
import './catalogueIconImg.css'

/**
 * The catalogue's `icon_url` drawn as an `<img>` — the one component that
 * owns what every such image needs (#467, after #461 made the third
 * hand-rolled copy): the `src` check, the dark-theme
 * `--catalogue-icon-filter` (`catalogueIconImg.css`), and the load-failure
 * path.
 *
 * **Decorative, always.** `alt=""`: every caller sits inside a control or row
 * that already carries the name.
 *
 * **No usable URL renders `fallback`.** `null`, `undefined`, `''` — an
 * `<img src="">` re-requests the page in some browsers and draws a
 * broken-image box in all of them — and a URL that failed to load. The failed
 * URL is remembered rather than a boolean, so a record whose `icon_url`
 * changes gets a fresh attempt.
 *
 * `className` is appended to the shared one, for a consumer's own layout hook
 * or filter override.
 */
export function CatalogueIconImg({
  src,
  size,
  className,
  fallback = null,
}: {
  src: string | null | undefined
  size: number
  className?: string
  fallback?: ReactNode
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  if (src == null || src === '' || src === failedUrl) return <>{fallback}</>
  return (
    <img
      className={className ? `catalogue-icon-img ${className}` : 'catalogue-icon-img'}
      src={src}
      alt=""
      width={size}
      height={size}
      // A long table of rows and a picker of 1,518: only fetch what is on
      // screen.
      loading="lazy"
      decoding="async"
      onError={() => setFailedUrl(src)}
      referrerPolicy="no-referrer"
    />
  )
}
